import BackToCPQButton from "../../Components/backToCPQButton";
import PreviousPageButtonCPQ from "../../Components/PreviousPageButtonCPQ";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Button,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Box,
    Grid,
    Snackbar,
    Alert,
    CircularProgress,
    InputAdornment,
    Divider,
    MenuItem,
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    Description as DescriptionIcon,
} from "@mui/icons-material";
import apiClient from "../../Helpers/apiClient";
import DocumentAssociation from "../DocumentManagerPage/DocumentAssociation";

const INITIAL_FORM_DATA = {
    id: 0,
    modelNumber: "",
    mixerType: "",
    number: 0,
    description: "",
    price: 0,
};

const NUMERIC_FIELDS = ["number", "price"];

const SORTABLE_COLUMNS = [
    { id: "id", label: "ID" },
    { id: "modelNumber", label: "Model Number" },
    { id: "mixerType", label: "Mixer Type" },
    { id: "number", label: "Number" },
    { id: "description", label: "Description" },
    { id: "price", label: "Price" },
];

function MixerPriceTable() {
    const [mixers, setMixers] = useState([]);
    const [filteredMixers, setFilteredMixers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [formData, setFormData] = useState(INITIAL_FORM_DATA);
    const [selectedMixer, setSelectedMixer] = useState(null);

    // Dialog states
    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [documentDialogOpen, setDocumentDialogOpen] = useState(false);

    // Sorting and filtering
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("id");
    const [filterText, setFilterText] = useState("");
    const [filterField, setFilterField] = useState("all");

    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    useEffect(() => {
        fetchMixers();
    }, []);

    useEffect(() => {
        applyFilters();
    }, [mixers, filterText, filterField]);

    const fetchMixers = async () => {
        setLoading(true);
        try {
            const response = await apiClient.get("/api/PumpStationPriceTables/MixerPrices");
            setMixers(Array.isArray(response.data) ? response.data : []);
            setError(null);
        } catch (err) {
            setError(`Failed to load data: ${err.message}`);
            setMixers([]);
        } finally {
            setLoading(false);
        }
    };

    // Helper function to get property value (handles both camelCase and PascalCase)
    const getPropValue = (obj, propName) => {
        const camelCase = propName;
        const pascalCase = propName.charAt(0).toUpperCase() + propName.slice(1);
        return obj[camelCase] !== undefined ? obj[camelCase] : obj[pascalCase];
    };

    const handleOpenDialog = (mixer = null) => {
        if (mixer) {
            setFormData(
                SORTABLE_COLUMNS.reduce((acc, col) => {
                    acc[col.id] = getPropValue(mixer, col.id) || (NUMERIC_FIELDS.includes(col.id) ? 0 : "");
                    return acc;
                }, {})
            );
            setSelectedMixer(mixer);
        } else {
            setFormData(INITIAL_FORM_DATA);
            setSelectedMixer(null);
        }
        setOpenDialog(true);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: NUMERIC_FIELDS.includes(name) ? parseFloat(value) || 0 : value,
        }));
    };

    const handleSubmit = async () => {
        try {
            // Convert to PascalCase for API
            const payload = {
                ...Object.keys(formData).reduce((acc, key) => {
                    acc[key.charAt(0).toUpperCase() + key.slice(1)] = formData[key];
                    return acc;
                }, {}),
                Quotations: [],
            };

            const url = selectedMixer
                ? `/api/PumpStationPriceTables/MixerPrices/${payload.Id}`
                : "/api/PumpStationPriceTables/MixerPrices";

            selectedMixer ? await apiClient.put(url, payload) : await apiClient.post(url, payload);

            setSnackbar({
                open: true,
                message: `Mixer ${selectedMixer ? "updated" : "created"} successfully!`,
                severity: "success",
            });

            setOpenDialog(false);
            fetchMixers();
        } catch (err) {
            setSnackbar({
                open: true,
                message: `Error: ${err.response?.data?.message || "Failed to save"}`,
                severity: "error",
            });
        }
    };

    const handleDelete = async () => {
        try {
            await apiClient.delete(
                `/api/PumpStationPriceTables/MixerPrices/${getPropValue(selectedMixer, "id")}`
            );
            setSnackbar({ open: true, message: "Mixer deleted successfully!", severity: "success" });
            setOpenDeleteDialog(false);
            fetchMixers();
        } catch (err) {
            setSnackbar({
                open: true,
                message: `Error: ${err.response?.data?.message || "Failed to delete"}`,
                severity: "error",
            });
        }
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const getSortedData = (data) => {
        if (!data.length) return [];
        return [...data].sort((a, b) => {
            let valueA = getPropValue(a, orderBy) || (NUMERIC_FIELDS.includes(orderBy) ? 0 : "");
            let valueB = getPropValue(b, orderBy) || (NUMERIC_FIELDS.includes(orderBy) ? 0 : "");

            if (NUMERIC_FIELDS.includes(orderBy) || orderBy === "id") {
                valueA = parseFloat(valueA) || 0;
                valueB = parseFloat(valueB) || 0;
                return order === "asc" ? valueA - valueB : valueB - valueA;
            }

            const strA = String(valueA).toLowerCase();
            const strB = String(valueB).toLowerCase();
            return order === "asc" ? strA.localeCompare(strB) : strB.localeCompare(strA);
        });
    };

    const applyFilters = () => {
        if (!filterText.trim()) {
            setFilteredMixers(mixers);
            return;
        }

        const searchTerm = filterText.toLowerCase();
        const filtered = mixers.filter((mixer) => {
            if (filterField === "all") {
                return SORTABLE_COLUMNS.some((col) =>
                    String(getPropValue(mixer, col.id) || "")
                        .toLowerCase()
                        .includes(searchTerm)
                );
            }

            return String(getPropValue(mixer, filterField) || "")
                .toLowerCase()
                .includes(searchTerm);
        });

        setFilteredMixers(filtered);
    };

    const displayData = getSortedData(filteredMixers);

    const formatPrice = (price) => {
        return typeof price === "number" ? price.toFixed(2) : "0.00";
    };

    return (
        <Box
            sx={{
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                padding: 4,
                paddingTop: 2,
                boxSizing: "border-box",
                overflow: "hidden",
            }}
        >
            <Typography variant="h4" gutterBottom align="center">
                Mixer Price Table
            </Typography>

            <Paper
                sx={{
                    width: "100%",
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    mb: 2,
                }}
            >
                <Box sx={{ display: "flex", justifyContent: "space-between", p: 2, alignItems: "center" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "60%" }}>
                        <TextField
                            variant="outlined"
                            placeholder="Search..."
                            size="small"
                            fullWidth
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: filterText ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setFilterText("");
                                                setFilterField("all");
                                            }}
                                        >
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                        <TextField
                            select
                            size="small"
                            value={filterField}
                            onChange={(e) => setFilterField(e.target.value)}
                            sx={{ minWidth: 120 }}
                        >
                            <MenuItem value="all">All Fields</MenuItem>
                            {SORTABLE_COLUMNS.map((col) => (
                                <MenuItem key={col.id} value={col.id}>
                                    {col.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                        Add New Mixer
                    </Button>
                </Box>

                <Divider />

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                ) : (
                    <>
                        <TableContainer sx={{ flex: 1, overflow: "auto" }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {SORTABLE_COLUMNS.map((col) => (
                                            <TableCell key={col.id}>
                                                <TableSortLabel
                                                    active={orderBy === col.id}
                                                    direction={orderBy === col.id ? order : "asc"}
                                                    onClick={() => handleRequestSort(col.id)}
                                                >
                                                    <strong>{col.label}</strong>
                                                </TableSortLabel>
                                            </TableCell>
                                        ))}
                                        <TableCell align="center">
                                            <strong>Actions</strong>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {displayData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={SORTABLE_COLUMNS.length + 1} align="center">
                                                {filterText ? "No matches found" : "No data available"}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        displayData.map((mixer) => (
                                            <TableRow key={getPropValue(mixer, "id")} hover>
                                                {SORTABLE_COLUMNS.map((col) => (
                                                    <TableCell key={col.id}>
                                                        {col.id === "price"
                                                            ? `$${formatPrice(getPropValue(mixer, col.id))}`
                                                            : getPropValue(mixer, col.id) ||
                                                              (NUMERIC_FIELDS.includes(col.id) ? 0 : "-")}
                                                    </TableCell>
                                                ))}
                                                <TableCell align="center">
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => handleOpenDialog(mixer)}
                                                        size="small"
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        color="secondary"
                                                        onClick={() => {
                                                            setSelectedMixer(mixer);
                                                            setDocumentDialogOpen(true);
                                                        }}
                                                        size="small"
                                                        title="Manage Documents"
                                                    >
                                                        <DescriptionIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => {
                                                            setSelectedMixer(mixer);
                                                            setOpenDeleteDialog(true);
                                                        }}
                                                        size="small"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box
                            sx={{
                                p: 2,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                {filterText
                                    ? `Showing ${displayData.length} of ${mixers.length} items`
                                    : `Total: ${mixers.length} items`}
                            </Typography>
                        </Box>
                    </>
                )}
            </Paper>

            <Box sx={{ mt: 2, textAlign: "center", flexShrink: 0 }}>
                <Button variant="contained" component={Link} to="/pumpstation-price-tables">
                    Back to Pump Station Tables
                </Button>
            </Box>

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{selectedMixer ? "Edit Mixer" : "Add New Mixer"}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Model Number"
                                name="modelNumber"
                                value={formData.modelNumber}
                                onChange={handleFormChange}
                                helperText="e.g., MX-100, Optional field"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Mixer Type"
                                name="mixerType"
                                value={formData.mixerType}
                                onChange={handleFormChange}
                                required
                                helperText="e.g., Submersible Mixer"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Number"
                                name="number"
                                type="number"
                                value={formData.number}
                                onChange={handleFormChange}
                                required
                                helperText="Numeric identifier or quantity"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description"
                                name="description"
                                value={formData.description}
                                onChange={handleFormChange}
                                multiline
                                rows={3}
                                helperText="Optional description of the mixer"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Price"
                                name="price"
                                type="number"
                                value={formData.price}
                                onChange={handleFormChange}
                                required
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained">
                        {selectedMixer ? "Update" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this mixer? This action cannot be undone.
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Document Dialog */}
            <Dialog
                open={documentDialogOpen}
                onClose={() => setDocumentDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Document Management{" "}
                    {selectedMixer
                        ? `- ${getPropValue(selectedMixer, "mixerType")} (ID: ${getPropValue(
                              selectedMixer,
                              "id"
                          )})`
                        : ""}
                </DialogTitle>
                <DialogContent>
                    {selectedMixer && (
                        <DocumentAssociation
                            entityType="MixerPrice"
                            entityId={getPropValue(selectedMixer, "id")}
                            entityName={`${getPropValue(selectedMixer, "mixerType")} ${
                                getPropValue(selectedMixer, "modelNumber") || ""
                            }`}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDocumentDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default MixerPriceTable;
